import { supabase } from '../../../shared/lib/supabase';

/**
 * Check if user has teams set up
 */
export async function checkUserHasTeams(): Promise<boolean> {
  const { data: user } = await supabase.auth.getUser();
  if (!user.user) return false;

  try {
    const { data, error } = await supabase
      .from('team_members')
      .select('team_id')
      .eq('user_id', user.user.id)
      .limit(1);

    if (error) {
      console.error('Error checking team membership:', error);
      return false;
    }

    return data && data.length > 0;
  } catch (error) {
    console.error('Error in checkUserHasTeams:', error);
    return false;
  }
}

/**
 * Create a personal team for the current user if they don't have one
 */
export async function ensureUserHasPersonalTeam(): Promise<string | null> {
  const { data: user } = await supabase.auth.getUser();
  if (!user.user) {
    console.error('No authenticated user');
    return null;
  }

  try {
    // Check if user already has a personal team
    const { data: existingTeams, error: checkError } = await supabase
      .from('teams')
      .select('id')
      .eq('owner_id', user.user.id)
      .eq('is_personal', true)
      .limit(1);

    if (checkError) {
      console.error('Error checking existing teams:', checkError);
      // If teams table doesn't exist, throw specific error
      if (checkError.code === '42P01' || checkError.message?.includes('relation') && checkError.message?.includes('does not exist')) {
        throw new Error('Teams tables not found. Please run database migrations first.');
      }
      return null;
    }

    if (existingTeams && existingTeams.length > 0) {
      console.log('User already has a personal team');
      return existingTeams[0].id;
    }

    // Try to call the database function first
    try {
      const { data, error } = await supabase
        .rpc('create_personal_team', {
          user_id: user.user.id,
          user_email: user.user.email || ''
        });

      if (!error && data) {
        console.log('Created personal team for user via RPC:', data);
        return data;
      }
    } catch (rpcError) {
      console.log('RPC function not available, falling back to direct insert');
    }

    // Fallback: Create team directly if RPC doesn't exist
    const teamName = user.user.email ? `${user.user.email.split('@')[0]}'s team` : 'Personal Team';
    const slug = `personal-${user.user.id.substring(0, 8)}-${Date.now()}`;

    // Create the team
    const { data: newTeam, error: createError } = await supabase
      .from('teams')
      .insert({
        name: teamName,
        owner_id: user.user.id,
        slug: slug,
        is_personal: true
      })
      .select()
      .single();

    if (createError) {
      console.error('Error creating team:', createError);
      return null;
    }

    // Add user as team member
    const { error: memberError } = await supabase
      .from('team_members')
      .insert({
        team_id: newTeam.id,
        user_id: user.user.id
      });

    if (memberError) {
      console.error('Error adding team member:', memberError);
      // Don't fail if member addition fails, team was created
    }

    console.log('Created personal team for user via direct insert:', newTeam.id);
    return newTeam.id;
  } catch (error) {
    console.error('Error in ensureUserHasPersonalTeam:', error);
    if (error instanceof Error && error.message.includes('Teams tables not found')) {
      throw error; // Re-throw migration errors
    }
    return null;
  }
}

/**
 * Migrate existing projects to a team
 */
export async function migrateProjectsToTeam(teamId: string): Promise<boolean> {
  const { data: user } = await supabase.auth.getUser();
  if (!user.user) return false;

  try {
    // First check if user has any projects
    const { data: projects, error: checkError } = await supabase
      .from('projects')
      .select('id')
      .eq('user_id', user.user.id);

    if (checkError) {
      console.error('Error checking projects:', checkError);
      // If it's a column error, projects table might not have team_id yet
      if (checkError.message?.includes('column') && checkError.message?.includes('team_id')) {
        throw new Error('Projects table not updated. Please run the full migration SQL.');
      }
      return false;
    }

    if (!projects || projects.length === 0) {
      console.log('No projects to migrate');
      return true; // Not an error, just no projects
    }

    // Update all user's projects that don't have a team_id
    const { error } = await supabase
      .from('projects')
      .update({ team_id: teamId })
      .eq('user_id', user.user.id)
      .is('team_id', null);

    if (error) {
      console.error('Error migrating projects:', error);
      // Check if it's because team_id column doesn't exist
      if (error.message?.includes('column') && error.message?.includes('team_id')) {
        throw new Error('Projects table missing team_id column. Please run database migrations.');
      }
      return false;
    }

    console.log('Successfully migrated projects to team');
    return true;
  } catch (error) {
    console.error('Error in migrateProjectsToTeam:', error);
    if (error instanceof Error && error.message.includes('migration')) {
      throw error; // Re-throw migration errors
    }
    return false;
  }
}

/**
 * Complete migration for a user
 */
export async function migrateUserToTeams(): Promise<boolean> {
  try {
    // First ensure user has a personal team
    const personalTeamId = await ensureUserHasPersonalTeam();
    if (!personalTeamId) {
      console.error('Failed to create personal team');
      return false;
    }

    // Then migrate their projects
    const migrated = await migrateProjectsToTeam(personalTeamId);
    if (!migrated) {
      console.error('Failed to migrate projects');
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error in migrateUserToTeams:', error);
    
    // Re-throw specific migration errors for better UI feedback
    if (error instanceof Error) {
      if (error.message.includes('Teams tables not found') || 
          error.message.includes('migration') ||
          error.message.includes('team_id column')) {
        throw error;
      }
    }
    
    return false;
  }
}